import React, { useState, useMemo, useCallback, useDeferredValue, useRef } from 'react';
import useProgressiveList from '../../../hooks/useProgressiveList';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    InputAdornment,
    IconButton,
    FormControlLabel,
    Switch,
    Chip,
} from '@mui/material';
import {
    Search as SearchIcon,
    Close as CloseIcon,
    SwapVert as SwapVertIcon,
    ArrowUpward as ArrowUpIcon,
    ArrowDownward as ArrowDownIcon,
} from '@mui/icons-material';
import timesheetService from '../../../services/timesheetService';
import SelectedStrip from '../../shared/SelectedStrip';

const DIVISION_FILTER_STORAGE_KEY = 'timesheetJobsDivisionFilter';

// Колонки слева направо. wrap=true — длинные строки переносятся на новую строку.
// nowrap=true — однострочно (короткий контент: код, division).
const COLUMNS = [
    { key: 'code',     label: 'Job #',       sortable: true, nowrap: true  },
    { key: 'name',     label: 'Description', sortable: true, wrap: true    },
    { key: 'division', label: 'Div',         sortable: true, nowrap: true  },
    { key: 'customer', label: 'Customer',    sortable: true, wrap: true    },
    { key: 'location', label: 'Location',    sortable: true, wrap: true    },
];

// Метка alternative job — та же, что в списке локаций JHA.
const ALT_BADGE = {
    marginLeft: 8,
    fontSize: '0.7rem',
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    padding: '1px 6px',
    borderRadius: 4,
    fontWeight: 600,
};

// Метка кода работы (7888-1): строка — отдельная часть работы 7888.
const CODE_BADGE = { ...ALT_BADGE, backgroundColor: '#fef3c7', color: '#92400e' };

// Lightweight DOM row — без MUI-обёрток, чтобы 1000+ строк не лагали на iPad.
const JobRow = React.memo(function JobRow({ job, checked, onToggle, rowIdx }) {
    const td = (extra = {}, wrap = false) => ({
        padding: '6px 8px',
        borderBottom: '1px solid #eee',
        verticalAlign: 'middle',
        ...(wrap
            ? { whiteSpace: 'normal', wordBreak: 'break-word' }
            : { whiteSpace: 'nowrap' }),
        ...extra,
    });
    return (
        <tr
            onClick={() => onToggle(job.id)}
            style={{
                cursor: 'pointer',
                background: checked ? '#e8f1ff' : (rowIdx % 2 === 0 ? '#fff' : '#fafafa'),
                userSelect: 'none',
                WebkitTapHighlightColor: 'transparent',
            }}
        >
            <td style={{ padding: '4px', borderBottom: '1px solid #eee', textAlign: 'center', verticalAlign: 'middle' }}>
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => { e.stopPropagation(); onToggle(job.id); }}
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: 18, height: 18, accentColor: '#1976d2' }}
                />
            </td>
            <td style={td({ fontWeight: 700, color: '#1976d2', ...(job._isSub ? { paddingLeft: 20 } : {}) })}>
                {job._isSub && <span style={{ color: '#94a3b8', marginRight: 4 }}>↳</span>}
                {job.isAltJob ? job._code : `#${job._code}`}
                {job.isAltJob && <span style={ALT_BADGE}>ALT</span>}
                {job._isSub && <span style={CODE_BADGE}>CODE</span>}
            </td>
            <td style={td({}, true)}>{job._name}</td>
            <td style={td({ color: '#3c5a78' })}>{job._division}</td>
            <td style={td({}, true)}>{job._customer}</td>
            <td style={td({ color: '#666' }, true)}>{job._location}</td>
        </tr>
    );
});

function SortIcon({ state }) {
    if (state === 'asc') return <ArrowUpIcon sx={{ fontSize: 14, color: 'primary.main' }} />;
    if (state === 'desc') return <ArrowDownIcon sx={{ fontSize: 14, color: 'primary.main' }} />;
    return <SwapVertIcon sx={{ fontSize: 14, opacity: 0.35 }} />;
}

export default function SelectJobsModal({
    open,
    onClose,
    jobs,
    selectedIds,
    onSave,
    enableDivisionFilter = false,
    divisionFilterStorageKey = DIVISION_FILTER_STORAGE_KEY,
    hideJobsWithCostDoc = false,
}) {
    const [localSelected, setLocalSelected] = useState(new Set());
    const [search, setSearch] = useState('');
    const deferredSearch = useDeferredValue(search);
    const searchInputRef = useRef(null);
    const searchRef = useRef(search);
    searchRef.current = search;
    const selectedRef = useRef(localSelected);
    selectedRef.current = localSelected;
    // null = без сортировки (исходный порядок), иначе { field, dir: 'asc' | 'desc' }
    const [sort, setSort] = useState(null);
    // Показать только работы с кодами и сами коды (7888, 7888-1, 7888-2…).
    const [onlyWithCodes, setOnlyWithCodes] = useState(false);

    const userDivision = useMemo(
        () => (localStorage.getItem('employeeDivision') || '').trim().toLowerCase(),
        []
    );
    const userDivisionLabel = localStorage.getItem('employeeDivision') || '';

    const [onlyMyDivision, setOnlyMyDivision] = useState(() => {
        if (!enableDivisionFilter) return false;
        const stored = localStorage.getItem(divisionFilterStorageKey);
        return stored === null ? true : stored === '1';
    });

    const handleDivisionFilterChange = useCallback((checked) => {
        setOnlyMyDivision(checked);
        localStorage.setItem(divisionFilterStorageKey, checked ? '1' : '0');
    }, [divisionFilterStorageKey]);

    React.useEffect(() => {
        if (open) {
            setLocalSelected(new Set(selectedIds || []));
            setSearch('');
            setSort(null);
        }
    }, [open, selectedIds]);

    // Препроцессинг полей раз на jobs change. Это главный perf-выигрыш —
    // без этого поиск/сортировка пересчитывали бы все геттеры на каждый ввод символа.
    const indexedJobs = useMemo(() => {
        return jobs.map(j => {
            const code = timesheetService.getJobCode(j) || '';
            const name = timesheetService.getJobName(j) || '';
            const customer = timesheetService.getJobCustomer(j) || '';
            const division = (j.Division || j.division || '').trim();
            const location = (j.Location || j.location || j.JobAddress || j.jobAddress || '').trim();
            const codeLc = code.toLowerCase();
            const nameLc = name.toLowerCase();
            const customerLc = customer.toLowerCase();
            const divisionLc = division.toLowerCase();
            const locationLc = location.toLowerCase();
            return {
                ...j,
                _code: code, _codeLc: codeLc,
                _name: name, _nameLc: nameLc,
                _customer: customer, _customerLc: customerLc,
                _division: division, _divisionLc: divisionLc,
                _location: location, _locationLc: locationLc,
                // Один объединённый индекс — поиск по всем полям одной строкой.
                // У кода работы — ещё номер родителя: «7888» находит и 7888-1, 7888-2.
                _searchBlob: `${codeLc} ${nameLc} ${customerLc} ${divisionLc} ${locationLc} ${String(j.parentJob || '').toLowerCase()}`,
                _hasCost: !!j.hasCostDoc,
                _isSub: !!j.parentJobId,
            };
        });
    }, [jobs]);

    // Работы, у которых есть коды, — для фильтра «With job codes».
    const parentsWithCodes = useMemo(
        () => new Set(indexedJobs.filter(j => j._isSub).map(j => j.parentJobId)),
        [indexedJobs],
    );

    const filteredJobs = useMemo(() => {
        let list = indexedJobs;

        if (onlyWithCodes) {
            list = list.filter(j => j._isSub || parentsWithCodes.has(j.id));
        }

        // Alternative jobs (Training, Overhead…) общие для всех дивизионов —
        // фильтр «Only my division» их не прячет.
        if (enableDivisionFilter && onlyMyDivision && userDivision) {
            list = list.filter(j => j.isAltJob || j._divisionLc === userDivision);
        }

        if (hideJobsWithCostDoc) {
            list = list.filter(j => !j._hasCost || localSelected.has(j.id));
        }

        const term = deferredSearch.trim().toLowerCase();
        if (term) {
            list = list.filter(j => j._searchBlob.includes(term));
        }

        // Alternative jobs всегда сверху в порядке Seq, как в десктопном шаблоне
        // таймшита; сортировка по колонке переставляет только обычные работы.
        const pinned = list.filter(j => j.isAltJob);
        let rest = list.filter(j => !j.isAltJob);
        if (sort) {
            const lcKey = `_${sort.field}Lc`;
            const sign = sort.dir === 'desc' ? -1 : 1;
            rest = [...rest].sort((a, b) => sign * (a[lcKey] || '').localeCompare(b[lcKey] || ''));
        }

        return [...pinned, ...rest];
    }, [indexedJobs, deferredSearch, enableDivisionFilter, onlyMyDivision, userDivision, hideJobsWithCostDoc, localSelected, sort, onlyWithCodes, parentsWithCodes]);

    // Рисуем частями: 2400 работ разом подвешивали открытие окна (10/2026).
    const { visible: visibleJobs, hasMore: moreJobs, sentinelRef: jobsSentinel } = useProgressiveList(filteredJobs, {
        resetKey: `${open}|${deferredSearch}|${onlyMyDivision}|${onlyWithCodes}|${sort ? sort.field + sort.dir : ''}`,
    });

    // Полоса выбранных — в порядке выбора; работы, которых нет в справочнике,
    // пропускаем (нечего показать).
    const selectedChips = useMemo(() => {
        const byId = new Map(indexedJobs.map(j => [j.id, j]));
        return [...localSelected].map(id => byId.get(id)).filter(Boolean);
    }, [indexedJobs, localSelected]);

    const handleToggle = useCallback((id) => {
        setLocalSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const clearSearch = useCallback(() => {
        setSearch('');
        searchInputRef.current?.focus();
    }, []);

    // Отметили работу из результатов поиска — чистим поиск, чтобы сразу искать следующую
    // без стирания. Снятие отметки поиск не трогает.
    const handleRowToggle = useCallback((id) => {
        const adding = !selectedRef.current.has(id);
        handleToggle(id);
        if (adding && searchRef.current) clearSearch();
    }, [handleToggle, clearSearch]);

    const handleSort = useCallback((field) => {
        setSort(prev => {
            if (!prev || prev.field !== field) return { field, dir: 'asc' };
            if (prev.dir === 'asc') return { field, dir: 'desc' };
            return null; // desc → off
        });
    }, []);

    // Alternative jobs выбираются только поштучно: иначе «все работы дивизиона»
    // приносили бы в грид ещё и Training, Overhead и прочие колонки.
    const handleSelectAllVisible = () => {
        setLocalSelected(prev => {
            const next = new Set(prev);
            for (const j of filteredJobs) if (!j.isAltJob) next.add(j.id);
            return next;
        });
    };
    const handleClearAll = () => setLocalSelected(new Set());

    const handleSave = () => {
        onSave([...localSelected]);
        onClose();
    };

    const sortStateFor = (field) => sort && sort.field === field ? sort.dir : 'default';

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
            PaperProps={{ sx: { height: '90vh', maxHeight: 800 } }}>
            <DialogTitle sx={{ pb: 1 }}>Select Jobs</DialogTitle>
            <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
                {/* Filter bar */}
                <Box sx={{ px: 2, pt: 2, pb: 1, bgcolor: 'background.paper' }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search Job #, Description, Div, Customer, Location…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        inputRef={searchInputRef}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 20 }} />
                                </InputAdornment>
                            ),
                            endAdornment: search ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" edge="end" aria-label="Clear search" onClick={clearSearch}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', columnGap: 2 }}>
                    {parentsWithCodes.size > 0 && (
                        <FormControlLabel
                            control={<Switch size="small" checked={onlyWithCodes} onChange={(e) => setOnlyWithCodes(e.target.checked)} />}
                            label={<Typography variant="body2">With job codes</Typography>}
                        />
                    )}
                    {enableDivisionFilter && userDivision && (
                        <Box>
                            <FormControlLabel
                                control={
                                    <Switch
                                        size="small"
                                        checked={onlyMyDivision}
                                        onChange={(e) => handleDivisionFilterChange(e.target.checked)}
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        Only my division
                                        <Chip label={userDivisionLabel} size="small"
                                            sx={{ ml: 1, height: 18, fontSize: 11 }} />
                                    </Typography>
                                }
                            />
                        </Box>
                    )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                            {filteredJobs.length} {filteredJobs.length === 1 ? 'job' : 'jobs'}
                            {sort && ` · sorted by ${sort.field} ${sort.dir === 'asc' ? '↑' : '↓'}`}
                        </Typography>
                        <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                            {filteredJobs.some(j => !j.isAltJob) && (
                                <Button size="small" onClick={handleSelectAllVisible} variant="text">
                                    Select all visible
                                </Button>
                            )}
                            {localSelected.size > 0 && (
                                <Button size="small" onClick={handleClearAll} variant="text" color="secondary">
                                    Clear ({localSelected.size})
                                </Button>
                            )}
                        </Box>
                    </Box>

                    {/* Выбранные работы — видны, пока ищешь следующие; × снимает.
                        Полоса всегда на месте и одной строкой: появись она или
                        перенесись на вторую строку — список уехал бы вниз прямо
                        под пальцем, и следующий тап попал бы не в ту строку. */}
                    <SelectedStrip empty="No jobs selected yet — tap rows below" sx={{ mt: 1 }}>
                        {selectedChips.map(j => (
                            <Chip
                                key={j.id}
                                label={
                                    <span>
                                        <b>{j.isAltJob ? j._code : `#${j._code}`}</b>
                                        {j._name && <span style={{ color: '#64748b' }}> {j._name.length > 24 ? j._name.slice(0, 24) + '…' : j._name}</span>}
                                    </span>
                                }
                                onDelete={() => handleToggle(j.id)}
                                sx={{ bgcolor: '#e8f1ff', color: '#1565c0', flexShrink: 0 }}
                            />
                        ))}
                    </SelectedStrip>
                </Box>

                {/* Scrollable table */}
                <Box sx={{
                    flex: 1, minHeight: 0,
                    overflow: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    contain: 'strict',
                }}>
                    <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: 13,
                        tableLayout: 'auto',
                    }}>
                        <thead style={{
                            position: 'sticky', top: 0, zIndex: 2,
                            background: '#f1f5f9',
                        }}>
                            <tr style={{ height: 32 }}>
                                <th style={{ width: 36, padding: '0 4px',
                                    borderBottom: '2px solid #cbd5e1' }}></th>
                                {COLUMNS.map(col => {
                                    const state = sortStateFor(col.key);
                                    return (
                                        <th
                                            key={col.key}
                                            onClick={col.sortable ? () => handleSort(col.key) : undefined}
                                            style={{
                                                padding: '0 8px',
                                                textAlign: 'left',
                                                fontWeight: 600,
                                                fontSize: 12,
                                                color: state !== 'default' ? '#1976d2' : '#475569',
                                                borderBottom: '2px solid #cbd5e1',
                                                cursor: col.sortable ? 'pointer' : 'default',
                                                whiteSpace: 'nowrap',
                                                userSelect: 'none',
                                            }}
                                            title={col.sortable ? `Tap to sort by ${col.label}` : undefined}
                                        >
                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                                {col.label}
                                                {col.sortable && <SortIcon state={state} />}
                                            </Box>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredJobs.length === 0 ? (
                                <tr>
                                    <td colSpan={COLUMNS.length + 1}
                                        style={{ textAlign: 'center', padding: '32px 16px', color: '#94a3b8' }}>
                                        No jobs found
                                    </td>
                                </tr>
                            ) : (
                                visibleJobs.map((job, idx) => (
                                    <JobRow
                                        key={job.id}
                                        rowIdx={idx}
                                        job={job}
                                        checked={localSelected.has(job.id)}
                                        onToggle={handleRowToggle}
                                    />
                                ))
                            )}
                            {moreJobs && (
                                <tr ref={jobsSentinel} aria-hidden="true">
                                    <td colSpan={COLUMNS.length + 1} style={{ height: 32 }} />
                                </tr>
                            )}
                        </tbody>
                    </table>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} variant="contained">
                    Save ({localSelected.size})
                </Button>
            </DialogActions>
        </Dialog>
    );
}
